import { c as _c } from "react/compiler-runtime";
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { parseApiAsset, toBigInt, ZERO } from '@morpho-org/dapp-core';
import { useMemo } from 'react';
import type { CollateralExposure } from '@/components/common/CollateralExposureStack';
import type { VaultListItem } from '@/lib/procedures/vaultList.contract';
import { vaultIncludesDonations } from '@/config/donation-vaults';
import { aggregateVaultListItemWarnings } from '@/helpers/aggregateVaultListWarnings';
import { getParsedValue } from '@/helpers/formatters';
import { computeRewards } from '@/helpers/rewards';
import { isVaultV2RateCapped } from '@/helpers/vaultV2RateCap';
const parseUsdOrNull = (usd: number | null) => usd === null ? null : safeParseNumber(usd);
export const useCuratorVaultsTableRows = t0 => {
  const $ = _c(3);
  const {
    vaults
  } = t0;
  let t1;
  bb0: {
    if (!vaults) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    let t2;
    if ($[1] !== vaults) {
      t2 = vaults.map(_temp3);
      $[1] = vaults;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = t2;
  }
  return t1;
};
function _temp(reward) {
  return computeRewards({
    rewardToken: {
      address: reward.asset.address,
      symbol: reward.asset.symbol,
      decimals: reward.asset.decimals,
      logoURI: reward.asset.logoUri,
      chain: {
        id: reward.asset.chainId
      }
    },
    apr: reward.supplyApr
  });
}
function _temp2(r) {
  return r.apr && r.apr > ZERO;
}
function _temp3(vault) {
  const asset = parseApiAsset({
    address: vault.asset.address,
    symbol: vault.asset.symbol,
    name: vault.asset.name ?? undefined,
    decimals: vault.asset.decimals,
    logoURI: vault.asset.logoUri,
    priceUsd: vault.asset.priceUsd,
    yield: vault.asset.yield,
    chain: {
      id: vault.chainId
    }
  });
  const rewards = vault.rewards.map(_temp).filter(_temp2);
  const collateralExposures = vault.exposure.map(exposure => {
    const exposureAsset = exposure.collateralAsset ?? vault.asset;
    return {
      token: parseApiAsset({
        address: exposureAsset.address,
        symbol: exposureAsset.symbol,
        logoURI: exposureAsset.logoUri,
        decimals: exposureAsset.decimals,
        chain: {
          id: vault.chainId
        }
      }),
      supplyAssetsUsd: parseUsdOrNull(exposure.exposureUsd),
      ...(!exposure.collateralAsset && {
        isIdle: true
      })
    };
  });
  const totalAssets = toBigInt(vault.totalAssets);
  const totalAssetsUsd = parseUsdOrNull(vault.totalAssetsUsd);
  const warnings = aggregateVaultListItemWarnings(vault.warnings);
  const liquidity = toBigInt(vault.liquidity);
  const liquidityUsd = parseUsdOrNull(vault.liquidityUsd);
  const apy = getParsedValue(vault.netApyExcludingRewards, safeParseNumber);
  const netApy = getParsedValue(vault.netApy, safeParseNumber);
  const performanceFee = getParsedValue(vault.performanceFee, safeParseNumber);
  const managementFee = getParsedValue(vault.managementFee, safeParseNumber);
  return {
    network: {
      chainId: vault.chainId
    },
    vault: {
      address: vault.address,
      name: vault.name,
      asset,
      warnings,
      version: vault.version,
      chainId: vault.chainId
    },
    deposits: {
      amount: totalAssets,
      valueUsd: totalAssetsUsd,
      asset,
      vaultAddress: vault.address,
      vaultVersion: vault.version,
      chainId: vault.chainId
    },
    liquidity: {
      amount: liquidity,
      valueUsd: liquidityUsd,
      asset
    },
    exposure: {
      collateralExposures,
      totalAssetsUsd
    },
    apy: {
      apy,
      netApy,
      performanceFee,
      managementFee,
      rewards,
      maxApy: vault.maxRate,
      isRateCapped: isVaultV2RateCapped(vault.apyBreakdown?.apy ?? vault.apy, vault.maxRate),
      includesDonations: vaultIncludesDonations(vault.chainId, vault.address),
      apyBreakdown: vault.version === "2.0" ? vault.apyBreakdown : undefined,
      points: [],
      asset
    }
  };
}